import { useState, useCallback, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import {
  Download, CheckCircle, AlertCircle, Loader2,
  Scissors, Play, Pause, Plus, X, RotateCcw, Grid, List,
  Import, Upload, Save, MessageSquare, ChevronLeft, FolderOpen
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { useSubscription } from '@/hooks/useSubscription';
import { useMovConversion } from '@/hooks/useMovConversion';
import { useAuth } from '@/hooks/useAuth';
import { CastingVideoImporter } from './CastingVideoImporter';
import LibraryPicker, { type StagedLibraryFile } from './library/LibraryPicker';
import { Slider } from '@/components/ui/slider';
import { UpgradeModal } from './subscription';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Trash2 } from 'lucide-react';
import { resolveVideoUrl } from '@/lib/utils';

// File size limit for non-subscribers (10MB)
const MAX_FILE_SIZE_FREE = 10 * 1024 * 1024;

interface SplitPoint {
  id: string;
  time: number;
  label: string;
}

interface ProjectFile {
  id: string;
  name: string;
  thumbnailUrl?: string;
  duration: number;
  timestamp?: string;
  projectName?: string;
  sessionName?: string;
  groupName?: string;
  takeName?: string;
  talent?: string[];
  s3Url?: string;
  s3Key?: string; // S3 key for refreshing presigned URLs
  s3Bucket?: string; // S3 bucket for imported videos
  imported?: boolean;
  liked?: boolean;
  comments?: any[];
  groupIndex?: number;
  takeNumber?: number;
  processingType?: 'split' | 'trimmed' | null;
  localFile?: File; // Original File object for local uploads (not serialized)
}

export const VideoSplitterV72 = () => {
  const [files, setFiles] = useState<ProjectFile[]>([]);
  const [selectedFile, setSelectedFile] = useState<ProjectFile | null>(null);
  const [splitPoints, setSplitPoints] = useState<SplitPoint[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isTrimmerMode, setIsTrimmerMode] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showComments, setShowComments] = useState(true);
  const [showImporter, setShowImporter] = useState(false);
  const [showLibraryPicker, setShowLibraryPicker] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentProject, setCurrentProject] = useState<string | null>(null);
  const [projects, setProjects] = useState<string[]>([]);

  // Trim mode states
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [trimLabel, setTrimLabel] = useState('');

  // Operation processing states
  const [processingOperations, setProcessingOperations] = useState<any[]>([]);
  const [completedOperations, setCompletedOperations] = useState<any[]>([]);

  const videoRef = useRef<HTMLVideoElement>(null);
  const { toast } = useToast();
  const { isSubscriber } = useSubscription();
  const { needsConversion, convert } = useMovConversion();
  const { getAuthHeaders } = useAuth();

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Process operation immediately
  const processOperation = async (operation: any) => {
    const opId = `op-${Date.now()}-${Math.random()}`;
    const newOp = {
      id: opId,
      ...operation,
      status: 'processing',
      progress: 0,
      startedAt: new Date().toISOString()
    };
    
    // Add to processing list
    setProcessingOperations(prev => [...prev, newOp]);
    
    toast({
      title: "Processing Started",
      description: `${operation.type} operation started for ${operation.fileName}`,
    });
    
    try {
      // Call the batch endpoint with a single operation
      const response = await fetch('/api/batch-split-trim', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          operations: [operation]
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Processing failed');
      }
      
      const data = await response.json();
      const result = data.results?.[0] || data;
      
      // Update operation status
      setProcessingOperations(prev => prev.filter(op => op.id !== opId));
      setCompletedOperations(prev => [...prev, {
        ...newOp,
        ...result,
        status: 'completed',
        completedAt: new Date().toISOString()
      }]);
      
      // Add to project files if successful
      if (result.status === 'completed' && result.segments) {
        if (operation.type === 'trim') {
          // For trim operations, replace the original file
          const trimmedSegment = result.segments[0];
          if (trimmedSegment && operation.originalFile) {
            const updatedFiles = files.map(f => 
              f.id === operation.originalFile.id 
                ? {
                    ...f,
                    s3Url: trimmedSegment.url,
                    s3Key: trimmedSegment.key,
                    duration: (operation.endTime || f.duration) - (operation.startTime || 0),
                    timestamp: new Date().toISOString(),
                    processingType: 'trimmed' as 'split' | 'trimmed'
                  }
                : f
            );
            setFiles(updatedFiles);
            if (currentProject) {
              saveProjectData(currentProject, updatedFiles);
            }
          }
        } else if (operation.type === 'split') {
          // For split operations, create new files in the same group
          const newFiles: ProjectFile[] = result.segments.map((segment: any, index: number) => ({
            id: segment.key || `${opId}-segment-${index}`,
            name: `${operation.originalFile?.takeName || operation.fileName} - Part ${index + 1}`,
            s3Url: segment.url,
            s3Key: segment.key,
            thumbnailUrl: operation.originalFile?.thumbnailUrl,
            duration: segment.endTime !== 'end' ? segment.endTime - segment.startTime : 0,
            timestamp: new Date().toISOString(),
            projectName: operation.originalFile?.projectName || currentProject,
            sessionName: operation.originalFile?.sessionName,
            groupName: operation.originalFile?.groupName,
            takeName: `${operation.originalFile?.takeName || operation.fileName} - Part ${index + 1}`,
            talent: operation.originalFile?.talent,
            groupIndex: operation.originalFile?.groupIndex,
            takeNumber: (operation.originalFile?.takeNumber || 0) + (index + 1) * 0.01,
            imported: operation.originalFile?.imported || false,
            processingType: 'split',
            liked: false,
            comments: []
          }));
          
          if (newFiles.length > 0) {
            const updatedFiles = [...files, ...newFiles];
            setFiles(updatedFiles);
            if (currentProject) {
              saveProjectData(currentProject, updatedFiles);
            }
          }
        }
      }
      
      toast({
        title: "Processing Complete",
        description: `${operation.type} operation completed for ${operation.fileName}`,
      });
      
    } catch (error) {
      console.error('Operation failed:', error);
      
      // Update operation status to failed
      setProcessingOperations(prev => prev.filter(op => op.id !== opId));
      setCompletedOperations(prev => [...prev, {
        ...newOp,
        status: 'failed',
        error: error.message,
        completedAt: new Date().toISOString()
      }]);
      
      toast({
        title: "Processing Failed",
        description: error.message || 'Failed to process operation',
        variant: "destructive"
      });
    }
  };

  // Upload a local file to S3 and return the key
  const uploadLocalFile = async (file: ProjectFile): Promise<{ s3Key: string; s3Bucket: string } | null> => {
    if (!file.localFile) {
      console.error('No local file to upload');
      return null;
    }

    try {
      // Get upload URL
      const uploadUrlResponse = await fetch('/get-upload-url', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          fileName: file.name,
          fileType: file.localFile.type || 'video/mp4',
          ephemeral: true // Use ephemeral bucket for one-off processing
        })
      });

      if (!uploadUrlResponse.ok) {
        throw new Error('Failed to get upload URL');
      }

      const { uploadUrl, key } = await uploadUrlResponse.json();

      // Upload to S3
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file.localFile,
        headers: {
          'Content-Type': file.localFile.type || 'video/mp4'
        }
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload video to S3');
      }

      console.log(`✅ Uploaded ${file.name} to S3: ${key}`);

      // Convert non-MP4 files (e.g. iPhone MOV) via MediaConvert
      if (needsConversion(file.name)) {
        toast({ title: 'Converting MOV to MP4...', description: 'This may take a minute' });
        const result = await convert(key);
        console.log(`✅ Converted ${file.name} to MP4: ${result.outputKey}`);
        return { s3Key: result.outputKey, s3Bucket: 'hey-joe-video-rotator' };
      }

      return { s3Key: key, s3Bucket: 'hey-joe-video-rotator' }; // ephemeral bucket
    } catch (error) {
      console.error('Upload failed:', error);
      throw error;
    }
  };

  // Clear completed operations
  const clearCompletedOperations = () => {
    setCompletedOperations([]);
  };


  // Clear all operations
  const clearAllOperations = () => {
    setProcessingOperations([]);
    setCompletedOperations([]);
  };

  const handleVideosImported = (videos: any[]) => {
    // Extract project name from first video
    const projectName = videos[0]?.castingData?.projectName || videos[0]?.projectName || 'Untitled Project';
    const sessionName = videos[0]?.castingData?.sessionName || videos[0]?.sessionName || 'Session';
    const fullProjectName = `${projectName} - ${sessionName}`;
    
    // Create/update project
    if (!projects.includes(fullProjectName)) {
      const updatedProjects = [...projects, fullProjectName];
      setProjects(updatedProjects);
      localStorage.setItem('videoProjects', JSON.stringify(updatedProjects));
    }
    
    // Set as current project and clear files if switching projects
    if (currentProject !== fullProjectName) {
      setFiles([]); // Clear files when switching to a different project
    }
    setCurrentProject(fullProjectName);
    localStorage.setItem('currentProject', fullProjectName);
    
    // First, sort videos by order field and remove duplicates
    const videoMap = new Map<string, any>();
    videos.forEach(video => {
      const key = video.id || video.s3Key || video.uri;
      // Only add if not already in map (removes duplicates)
      if (!videoMap.has(key)) {
        videoMap.set(key, video);
      }
    });
    
    // Convert back to array and sort by order
    const sortedVideos = Array.from(videoMap.values()).sort((a, b) => {
      const orderA = a.order !== undefined ? a.order : (a.castingData?.order !== undefined ? a.castingData.order : 999);
      const orderB = b.order !== undefined ? b.order : (b.castingData?.order !== undefined ? b.castingData.order : 999);
      return orderA - orderB;
    });
    
    // Group videos by their original groupId from the API (to preserve separate groups even with same talent)
    const groupMap = new Map<string, any[]>();
    sortedVideos.forEach((video, index) => {
      // Use the original groupId if available, otherwise fall back to talent-based grouping
      const groupKey = video.groupId || video.groupTalents?.map((t: any) => t.name).sort().join(',') || `Solo_${index}`;
      if (!groupMap.has(groupKey)) {
        groupMap.set(groupKey, []);
      }
      groupMap.get(groupKey)?.push(video);
    });
    
    const newFiles: ProjectFile[] = [];
    
    groupMap.forEach((groupVideos, groupKey) => {
      // Extract the actual group number from groupId (e.g., "group-15" -> 15)
      // or from groupOrder if available
      let groupNumber = 0;
      if (groupVideos[0]?.groupOrder !== undefined) {
        groupNumber = groupVideos[0].groupOrder;
      } else if (groupKey.startsWith('group-')) {
        groupNumber = parseInt(groupKey.split('-')[1]) || 0;
      } else {
        // Fallback to sequential numbering if no group number found
        groupNumber = Array.from(groupMap.keys()).indexOf(groupKey) + 1;
      }
      const talentNames = groupVideos[0].groupTalents?.map((t: any) => t.name) || [groupVideos[0].talentName];
      
      // Sort group videos by order as well
      groupVideos.sort((a, b) => {
        const orderA = a.order || a.castingData?.order || 999;
        const orderB = b.order || b.castingData?.order || 999;
        return orderA - orderB;
      });
      
      groupVideos.forEach(video => {
        // Try multiple possible URL fields
        const videoUrl = video.s3Url || video.url || video.videoUrl || video.fileUrl || video.cloudFrontUrl;
        
        // Use takeNumber from import (which is sequential 1, 2, 3...)
        const takeNum = video.takeNumber || video.castingData?.takeNumber || video.order || video.castingData?.order;
        
        newFiles.push({
          id: video.s3Key || Math.random().toString(36).substr(2, 9),
          name: video.name,
          thumbnailUrl: video.thumbnailUrl,
          duration: video.duration || 0,
          projectName: video.castingData?.projectName || video.projectName,
          sessionName: video.castingData?.sessionName || video.sessionName,
          groupName: `Group ${groupNumber}`,
          takeName: `Take ${takeNum}`,
          talent: talentNames,
          s3Url: videoUrl,
          s3Key: video.s3Key, // Store the S3 key for URL refresh
          s3Bucket: video.s3Bucket || 'hey-joe-videos', // Store the bucket
          imported: true,
          liked: video.castingData?.liked || video.liked || false,
          comments: video.castingData?.comments || video.comments || [],
          groupIndex: groupNumber,
          takeNumber: takeNum
        });
      });
    });
    
    // If we switched projects, files was already cleared, so just use newFiles
    // If same project, append to existing files
    const updatedFiles = currentProject === fullProjectName ? [...files, ...newFiles] : newFiles;
    setFiles(updatedFiles);
    saveProjectData(fullProjectName, updatedFiles);
    
    if (newFiles.length > 0) {
      setSelectedFile(newFiles[0]);
      loadVideo(newFiles[0]);
    }
    
    toast({
      title: 'Videos Imported',
      description: `${videos.length} video${videos.length > 1 ? 's' : ''} imported to ${fullProjectName}`,
    });
  };

  const loadVideo = async (file: ProjectFile) => {
    // Always set the selected file, even if video ref isn't ready yet
    setSelectedFile(file);
    setSplitPoints([]);
    setCurrentTime(0);
    
    if (isTrimmerMode) {
      setTrimStart(0);
      setTrimEnd(file.duration || 0);
    }
    
    // Check if this is an imported video with s3Key and potentially expired URL
    if (file.s3Key && file.s3Url) {
      try {
        // Try to refresh the URL - it might be expired if imported hours ago
        const response = await fetch('/api/casting/refresh-video-url', {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify({ s3Key: file.s3Key })
        });

        if (response.ok) {
          const data = await response.json();
          file.s3Url = data.downloadUrl;
          console.log('Refreshed video URL successfully');
          
          // Update the file in the files array too
          setFiles(prevFiles => 
            prevFiles.map(f => f.id === file.id ? { ...f, s3Url: data.downloadUrl } : f)
          );
        }
      } catch (error) {
        console.log('Using existing URL, refresh failed:', error);
      }
    }
    
    // Load the video if the ref is ready
    const videoUrl = resolveVideoUrl(file.s3Url, file.s3Key) || file.s3Url;
    if (videoRef.current && videoUrl) {
      videoRef.current.src = videoUrl;
      videoRef.current.load();
    }
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const videoFiles = acceptedFiles.filter(file => file.type.startsWith('video/'));

    if (videoFiles.length === 0) {
      toast({
        title: "Invalid files",
        description: "Please select video files only",
        variant: "destructive"
      });
      return;
    }

    // Check file size limit for non-subscribers - show upgrade modal
    if (!isSubscriber) {
      const oversizedFiles = videoFiles.filter(file => file.size > MAX_FILE_SIZE_FREE);
      if (oversizedFiles.length > 0) {
        setShowUpgradeModal(true);
        return;
      }
    }

    const newFiles: ProjectFile[] = videoFiles.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      name: file.name,
      duration: 0,
      s3Url: URL.createObjectURL(file),
      imported: false,
      localFile: file // Store the File object for later upload
    }));

    const updatedFiles = [...files, ...newFiles];
    setFiles(updatedFiles);
    if (currentProject) {
      saveProjectData(currentProject, updatedFiles);
    }
    if (newFiles.length > 0) {
      setSelectedFile(newFiles[0]);
      loadVideo(newFiles[0]);
    }
  }, [toast, files, currentProject]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'video/*': ['.mp4', '.mov', '.avi', '.mkv', '.webm']
    }
  });

  // "Choose from your library" — the staged copy lives in hey-joe-video-rotator,
  // so we store the bucket-prefixed key (playback proxy + URL refresh both parse
  // it) and strip the prefix again when building processing operations.
  const handleLibraryPick = (f: StagedLibraryFile) => {
    const newFile: ProjectFile = {
      id: f.key,
      name: f.name,
      duration: 0,
      s3Url: f.downloadUrl,
      s3Key: `hey-joe-video-rotator/${f.key}`,
      s3Bucket: 'hey-joe-video-rotator',
      imported: true
    };
    const updatedFiles = [...files, newFile];
    setFiles(updatedFiles);
    if (currentProject) {
      saveProjectData(currentProject, updatedFiles);
    }
    loadVideo(newFile);
  };

  // Check authentication status and load projects on mount
  useEffect(() => {
    // ONLY use cookie token - single source of truth
    const cookieToken = document.cookie
      .split('; ')
      .find(row => row.startsWith('token='))
      ?.split('=')[1];
    setIsAuthenticated(!!cookieToken);
    
    // Clear corrupted localStorage data from before S3 key fix
    const dataVersion = localStorage.getItem('videoSplitterDataVersion');
    if (dataVersion !== 'v2-s3-fix') {
      console.log('Clearing old localStorage data due to S3 key format changes');
      localStorage.removeItem('videoProjects');
      localStorage.removeItem('projectFiles');
      localStorage.removeItem('currentProject');
      localStorage.setItem('videoSplitterDataVersion', 'v2-s3-fix');
      setProjects([]);
      setCurrentProject(null);
      setFiles([]);
      return;
    }
    
    // Load saved projects and files from localStorage
    const savedProjects = JSON.parse(localStorage.getItem('videoProjects') || '[]');
    setProjects(savedProjects);
    
    const savedCurrentProject = localStorage.getItem('currentProject');
    if (savedCurrentProject) {
      setCurrentProject(savedCurrentProject);
      loadProjectFiles(savedCurrentProject);
    }
  }, []);
  
  // Save project data to localStorage with timestamp
  const saveProjectData = (projectName: string, projectFiles: ProjectFile[]) => {
    const projectData = JSON.parse(localStorage.getItem('projectFiles') || '{}');
    projectData[projectName] = {
      files: projectFiles,
      savedAt: new Date().toISOString()
    };
    localStorage.setItem('projectFiles', JSON.stringify(projectData));
  };
  
  // Load project files from localStorage and check for expiration
  const loadProjectFiles = (projectName: string) => {
    const projectData = JSON.parse(localStorage.getItem('projectFiles') || '{}');
    const project = projectData[projectName];
    
    if (!project) {
      setFiles([]);
      return;
    }
    
    // Check if data is older than 24 hours
    const savedAt = new Date(project.savedAt || 0);
    const now = new Date();
    const hoursSinceSave = (now.getTime() - savedAt.getTime()) / (1000 * 60 * 60);
    
    if (hoursSinceSave > 24) {
      // Data is expired, clear it out entirely
      delete projectData[projectName];
      localStorage.setItem('projectFiles', JSON.stringify(projectData));
      
      // Remove from projects list too
      const updatedProjects = projects.filter(p => p !== projectName);
      setProjects(updatedProjects);
      localStorage.setItem('videoProjects', JSON.stringify(updatedProjects));
      
      // Clear current project if it was this one
      if (currentProject === projectName) {
        setCurrentProject(null);
        localStorage.removeItem('currentProject');
      }
      
      setFiles([]);
      
      toast({
        title: "Project expired",
        description: "This project's videos have expired after 24 hours and has been removed. Please import videos to create a new project.",
        variant: "destructive"
      });
    } else {
      // Data is still valid
      setFiles(project.files || []);
    }
  };

  useEffect(() => {
    if (videoRef.current) {
      const video = videoRef.current;
      
      const handleLoadedMetadata = () => {
        setDuration(video.duration);
        if (isTrimmerMode) {
          setTrimEnd(video.duration);
        }
      };
      
      const handleTimeUpdate = () => {
        setCurrentTime(video.currentTime);
      };
      
      video.addEventListener('loadedmetadata', handleLoadedMetadata);
      video.addEventListener('timeupdate', handleTimeUpdate);
      
      // If we have a selected file and it's different from what's loaded, load it
      const resolvedUrl = resolveVideoUrl(selectedFile?.s3Url, selectedFile?.s3Key) || selectedFile?.s3Url;
      if (selectedFile && resolvedUrl && video.src !== resolvedUrl) {
        video.src = resolvedUrl;
        video.load();
      }
      
      return () => {
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
        video.removeEventListener('timeupdate', handleTimeUpdate);
      };
    }
  }, [videoRef.current, isTrimmerMode, selectedFile]);

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleSeek = (value: number[]) => {
    const newTime = value[0];
    if (videoRef.current) {
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const addSplitAtCurrentTime = () => {
    if (!selectedFile) return;
    
    const newSplit: SplitPoint = {
      id: Math.random().toString(36).substr(2, 9),
      time: currentTime,
      label: `Segment ${splitPoints.length + 1}`
    };
    
    setSplitPoints(prev => [...prev, newSplit].sort((a, b) => a.time - b.time));
  };

  const removeSplitPoint = (id: string) => {
    setSplitPoints(prev => prev.filter(point => point.id !== id));
  };

  const handleDeleteProject = (projectName: string) => {
    const updatedProjects = projects.filter(p => p !== projectName);
    setProjects(updatedProjects);
    localStorage.setItem('videoProjects', JSON.stringify(updatedProjects));
    
    // Remove project data from localStorage
    const projectData = JSON.parse(localStorage.getItem('projectFiles') || '{}');
    delete projectData[projectName];
    localStorage.setItem('projectFiles', JSON.stringify(projectData));
    
    if (currentProject === projectName) {
      setCurrentProject(null);
      localStorage.removeItem('currentProject');
      setFiles([]);
      setSelectedFile(null);
    }
    
    toast({
      title: 'Project Deleted',
      description: `${projectName} has been removed`,
    });
  };

  const clearAll = () => {
    setFiles([]);
    setSelectedFile(null);
    setSplitPoints([]);
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
    if (videoRef.current) {
      videoRef.current.src = '';
    }
    // Clear localStorage data
    if (currentProject) {
      const projectData = JSON.parse(localStorage.getItem('projectFiles') || '{}');
      delete projectData[currentProject];
      localStorage.setItem('projectFiles', JSON.stringify(projectData));
    }
  };

  const getSegmentDuration = (index: number): string => {
    const start = index === 0 ? 0 : splitPoints[index - 1].time;
    const end = index < splitPoints.length ? splitPoints[index].time : duration;
    return formatTime(end - start);
  };

  const handleImportClick = () => {
    // ONLY use cookie token - single source of truth
    const cookieToken = document.cookie
      .split('; ')
      .find(row => row.startsWith('token='))
      ?.split('=')[1];
    
    if (cookieToken) {
      // Clear any stale localStorage tokens that might confuse the importer
      localStorage.removeItem('castingAuthToken');
      // We have a valid cookie token, go straight to importer
      setShowImporter(true);
    } else {
      // No token found, need to login
      setShowLoginModal(true);
    }
  };

  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    setIsAuthenticated(true);
    // Don't auto-open importer - user may have been trying to upload, not import
    // Force AppHeader to refresh by dispatching a custom event
    window.dispatchEvent(new Event('auth-state-changed'));
    toast({
      title: "Signed in successfully",
      description: "You can now upload larger files or import from Hey Joe.",
    });
  };

  return (
    <>
      <main
        className="mx-auto"
        style={{
          maxWidth: 1440,
          padding: '0 16px 96px',
          background: 'var(--hj-bg)',
          color: 'var(--hj-ink)',
          minHeight: 'calc(100vh - 68px)',
        }}
      >
        <div className="flex items-center gap-2.5" style={{ padding: '20px 0 10px', fontSize: 13 }}>
          <Link to="/" className="inline-flex items-center gap-1.5" style={{ color: 'var(--hj-ink-3)', textDecoration: 'none' }}>
            <ChevronLeft size={12} strokeWidth={2} />
            Home
          </Link>
          <span style={{ color: 'var(--hj-ink-4)' }}>/</span>
          <span style={{ color: 'var(--hj-ink)' }}>Splitter</span>
        </div>

        <header
          style={{
            padding: '10px 0 28px',
            borderBottom: '1px solid var(--hj-hairline-soft)',
            marginBottom: 24,
          }}
        >
          <div className="mono-label" style={{ marginBottom: 10 }}>Splitter</div>
          <h1
            className="font-hj-display m-0"
            style={{
              fontSize: 'clamp(36px, 4.6vw, 56px)',
              lineHeight: 1,
              color: 'var(--hj-ink)',
              letterSpacing: '-0.025em',
            }}
          >
            Trim &amp; split.
          </h1>
          <p
            className="m-0"
            style={{
              marginTop: 12,
              color: 'var(--hj-ink-3)',
              fontSize: 14.5,
              maxWidth: 560,
              lineHeight: 1.5,
            }}
          >
            Cut a clip down to one take, or carve it into pieces at every split point.
          </p>
        </header>

        {/* Project Selector */}
        {projects.length > 0 && (
          <div
            className="flex items-center gap-3 flex-wrap"
            style={{
              marginBottom: 16,
              padding: 14,
              borderRadius: 'var(--hj-r-md)',
              background: 'var(--hj-surface-1)',
              border: '1px solid var(--hj-hairline)',
            }}
          >
            <Label htmlFor="project-select" className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>Project</Label>
            <Select value={currentProject || ''} onValueChange={(value) => {
              setCurrentProject(value);
              localStorage.setItem('currentProject', value);
              loadProjectFiles(value);
            }}>
              <SelectTrigger id="project-select" className="w-[300px]">
                <SelectValue placeholder="Select a project" />
              </SelectTrigger>
              <SelectContent>
                {projects.map(project => (
                  <SelectItem key={project} value={project}>
                    {project}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {currentProject && (
              <button
                type="button"
                onClick={() => handleDeleteProject(currentProject)}
                aria-label="Delete project"
                style={{
                  background: 'transparent',
                  border: '1px solid var(--hj-hairline)',
                  color: 'var(--hj-ink-3)',
                  borderRadius: 999,
                  padding: 8,
                  cursor: 'pointer',
                }}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            <div className="ml-auto inline-flex items-center gap-1.5" style={{ fontSize: 12, color: 'var(--hj-ink-3)' }}>
              <Save className="w-3.5 h-3.5" />
              Auto-saved
            </div>
          </div>
        )}

      {/* Upgrade Modal */}
      <UpgradeModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        trigger="video-tools"
      />

      <LibraryPicker
        open={showLibraryPicker}
        onClose={() => setShowLibraryPicker(false)}
        onPick={handleLibraryPick}
      />

      {/* Project Files Section with integrated upload */}
      <Card className="p-6 mb-6">
          {/* Header with integrated upload/import */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">
              Project Files {files.length > 0 && `(${files.length})`}
            </h2>
            <div className="flex items-center gap-2">
              {files.length > 0 && (
                <>
                  <Button
                    size="sm"
                    variant={viewMode === 'grid' ? 'default' : 'outline'}
                    onClick={() => setViewMode('grid')}
                  >
                    <Grid className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant={viewMode === 'list' ? 'default' : 'outline'}
                    onClick={() => setViewMode('list')}
                  >
                    <List className="w-4 h-4" />
                  </Button>
                </>
              )}
              <label className="inline-block">
                <input {...getInputProps()} />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => {
                    e.preventDefault();
                    const input = e.currentTarget.parentElement?.querySelector('input');
                    input?.click();
                  }}
                  type="button"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload
                </Button>
              </label>
              <Button
                size="sm"
                variant="outline"
                type="button"
                onClick={() => setShowLibraryPicker(true)}
              >
                <FolderOpen className="w-4 h-4 mr-2" />
                Choose from your library
              </Button>
              {files.some(f => f.comments && f.comments.length > 0) && (
                <Button
                  size="sm"
                  variant={showComments ? 'default' : 'outline'}
                  onClick={() => setShowComments(!showComments)}
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  {showComments ? 'Hide' : 'Show'} Comments
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={clearAll}
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Clear All
              </Button>
            </div>
          </div>

          {/* Upload area when no files */}
          {files.length === 0 && (
            <div {...getRootProps()} className="border-2 border-dashed border-border rounded-lg py-12 hover:border-primary/50 hover:bg-accent/5 transition-all cursor-pointer bg-background">
              <input {...getInputProps()} />
              <div className="flex flex-col items-center gap-3">
                <Upload className="w-10 h-10 text-muted-foreground" />
                <p className="text-base font-medium">Drop videos here or click to browse</p>
                <p className="text-sm text-muted-foreground">Drag and drop video files or click to upload</p>
              </div>
            </div>
          )}

          {/* Project/Session Header */}
          {files.length > 0 && files[0]?.projectName && (
            <div className="mb-4 text-sm text-muted-foreground">
              <span className="font-medium">{files[0].projectName}</span>
              {files[0].sessionName && <span> - {files[0].sessionName}</span>}
              <span className="ml-2">({files.length} videos)</span>
            </div>
          )}

          {/* Files Grid/List - Group by groupIndex */}
          {files.length > 0 && (
            <div className="space-y-4">
            {Array.from(new Set(files.map(f => f.groupIndex || 0))).sort().map(groupIdx => {
              const groupFiles = files.filter(f => (f.groupIndex || 0) === groupIdx);
              const groupTalent = groupFiles[0]?.talent || [];
              
              return (
                <div key={groupIdx} className="border rounded-lg p-4">
                  <h3 className="font-semibold mb-3">
                    Group {groupIdx}
                    <span className="ml-2 text-sm font-normal">
                      {groupFiles.length} takes
                    </span>
                    <div className="mt-1 text-sm font-normal">
                      {groupTalent.join(', ')} • PARTICIPANT
                    </div>
                  </h3>
                  
                  <div className={viewMode === 'grid' ? 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 lg:grid-cols-9 gap-2' : 'space-y-2'}>
                    {groupFiles.map(file => (
                      <div
                        key={file.id}
                        className={`relative cursor-pointer rounded-lg border ${
                          selectedFile?.id === file.id ? 'border-primary' : 'border-border'
                        } ${viewMode === 'grid' ? 'p-2' : 'p-3'} hover:border-primary/50 transition-colors`}
                        onClick={() => loadVideo(file)}
                      >
                        {file.liked && (
                          <span className="absolute top-2 right-2 text-red-500">❤️</span>
                        )}
                        {file.comments && file.comments.length > 0 && (
                          <span className="absolute top-2 right-8 text-blue-500">💬</span>
                        )}
                        {/* Show processing type labels */}
                        {file.processingType === 'split' && (
                          <span className="absolute top-2 left-2 text-xs px-2 py-1 rounded bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">
                            Split
                          </span>
                        )}
                        {file.processingType === 'trimmed' && (
                          <span className="absolute top-2 left-2 text-xs px-2 py-1 rounded bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                            Trimmed
                          </span>
                        )}
                        {/* Fallback for older files that use groupName */}
                        {!file.processingType && file.groupName?.includes('Split') && (
                          <span className="absolute top-2 left-2 text-xs px-2 py-1 rounded bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">
                            Split
                          </span>
                        )}
                        {!file.processingType && file.groupName?.includes('Trimmed') && (
                          <span className="absolute top-2 left-2 text-xs px-2 py-1 rounded bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                            Trimmed
                          </span>
                        )}
                
                        {viewMode === 'grid' ? (
                          <>
                            {/* Thumbnail */}
                            <div className="aspect-video bg-black rounded mb-1 flex items-center justify-center">
                              {file.thumbnailUrl ? (
                                <img src={file.thumbnailUrl} className="w-full h-full object-cover rounded" />
                              ) : (
                                <div className="text-white/50">No preview</div>
                              )}
                            </div>
                            <div className="space-y-1">
                              <p className="font-medium text-sm truncate">
                                {file.takeName}
                              </p>
                              {file.timestamp && <p className="text-xs text-muted-foreground">{file.timestamp}</p>}
                              {file.comments && file.comments.length > 0 && (
                                <p className="text-xs text-blue-600">{file.comments.length} comment(s)</p>
                              )}
                              {showComments && file.comments && file.comments.length > 0 && (
                                <div className="mt-2 text-xs space-y-1">
                                  {file.comments.map((comment: any, idx: number) => {
                                    let commentText = '';
                                    let author = '';
                                    
                                    if (typeof comment === 'string') {
                                      commentText = comment;
                                    } else if (comment?.content) {
                                      commentText = comment.content;
                                      // Try to extract author from created_by if available
                                      if (comment.created_by?.name) {
                                        author = comment.created_by.name;
                                      } else if (comment.created_by?.email) {
                                        author = comment.created_by.email.split('@')[0];
                                      }
                                    } else {
                                      commentText = comment?.text || comment?.message || '';
                                    }
                                    
                                    return (
                                      <div key={idx} className="bg-muted/50 p-2 rounded">
                                        {author && <div className="font-semibold text-xs mb-1">{author}:</div>}
                                        <div>{commentText}</div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </>
                        ) : (
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">{file.takeName}</p>
                              <p className="text-sm text-muted-foreground">{file.name}</p>
                              {file.comments && file.comments.length > 0 && (
                                <p className="text-xs text-blue-600">{file.comments.length} comment(s)</p>
                              )}
                              {showComments && file.comments && file.comments.length > 0 && (
                                <div className="mt-2 text-xs space-y-1">
                                  {file.comments.map((comment: any, idx: number) => {
                                    let commentText = '';
                                    let author = '';
                                    
                                    if (typeof comment === 'string') {
                                      commentText = comment;
                                    } else if (comment?.content) {
                                      commentText = comment.content;
                                      // Try to extract author from created_by if available
                                      if (comment.created_by?.name) {
                                        author = comment.created_by.name;
                                      } else if (comment.created_by?.email) {
                                        author = comment.created_by.email.split('@')[0];
                                      }
                                    } else {
                                      commentText = comment?.text || comment?.message || '';
                                    }
                                    
                                    return (
                                      <div key={idx} className="bg-muted/50 p-2 rounded">
                                        {author && <div className="font-semibold text-xs mb-1">{author}:</div>}
                                        <div>{commentText}</div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                            {file.timestamp && <span className="text-sm text-muted-foreground">{file.timestamp}</span>}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            </div>
          )}

          {files.length > 0 && files[0]?.imported && (
            <Alert className="mt-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Imported files from casting.heyjoe.io are cached for 24 hours. Re-import if files become unavailable.
              </AlertDescription>
            </Alert>
          )}
        </Card>

      {/* Main Video Editor */}
      {selectedFile && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Video Player */}
          <div className="lg:col-span-2">
            <Card className="p-6">
              {/* Mode Toggle */}
              <div className="flex items-center justify-center gap-4 mb-4 p-3 bg-muted/50 rounded-lg">
                <span className="text-sm font-medium">Mode:</span>
                <div className="flex items-center gap-2">
                  <span className={`text-sm ${!isTrimmerMode ? 'font-bold text-primary' : 'text-muted-foreground'}`}>
                    Split
                  </span>
                  <Switch
                    checked={isTrimmerMode}
                    onCheckedChange={setIsTrimmerMode}
                  />
                  <span className={`text-sm ${isTrimmerMode ? 'font-bold text-primary' : 'text-muted-foreground'}`}>
                    Trim
                  </span>
                </div>
              </div>

              {/* Video */}
              <div className="relative bg-black rounded-lg overflow-hidden mb-4" style={{ minHeight: '400px', maxHeight: '600px' }}>
                <video
                  ref={videoRef}
                  className="w-full h-full"
                  style={{ minHeight: '400px', maxHeight: '600px', objectFit: 'contain' }}
                  controls={true}
                  crossOrigin="anonymous"
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  onError={(e) => {
                    console.error('Video loading error:', e);
                  }}
                />
                
              </div>

              {/* Timeline */}
              <div className="space-y-2">
                <div className="flex items-center gap-4">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={togglePlayPause}
                  >
                    {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </Button>
                  
                  <span className="text-sm">{formatTime(currentTime)}</span>
                  
                  <div className="flex-1 relative">
                    <Slider
                      value={[currentTime]}
                      onValueChange={handleSeek}
                      max={duration}
                      step={0.1}
                      className="w-full"
                    />
                    
                    {/* Split markers */}
                    {!isTrimmerMode && splitPoints.map(point => (
                      <div
                        key={point.id}
                        className="absolute top-0 w-1 h-4 bg-primary"
                        style={{ left: `${(point.time / duration) * 100}%` }}
                      />
                    ))}
                    
                    {/* Trim markers */}
                    {isTrimmerMode && (
                      <>
                        {/* Trim start marker */}
                        <div
                          className="absolute top-0 w-1 h-4 bg-green-500"
                          style={{ left: `${(trimStart / duration) * 100}%` }}
                          title="Trim Start"
                        />
                        {/* Trim end marker */}
                        <div
                          className="absolute top-0 w-1 h-4 bg-red-500"
                          style={{ left: `${(trimEnd / duration) * 100}%` }}
                          title="Trim End"
                        />
                        {/* Highlight trimmed region */}
                        <div
                          className="absolute top-0 h-4 bg-primary/20"
                          style={{ 
                            left: `${(trimStart / duration) * 100}%`,
                            width: `${((trimEnd - trimStart) / duration) * 100}%`
                          }}
                        />
                      </>
                    )}
                  </div>
                  
                  <span className="text-sm">{formatTime(duration)}</span>
                </div>
              </div>

              {/* Casting Info */}
              {selectedFile.imported && (
                <div className="mt-4 p-4 bg-muted/50 rounded">
                  <div className="space-y-1">
                    <p className="text-sm font-medium">{selectedFile.groupName} - {selectedFile.takeName}</p>
                    {selectedFile.talent && selectedFile.talent.length > 0 && (
                      <p className="text-sm text-muted-foreground">
                        Talent: {selectedFile.talent.join(', ')}
                      </p>
                    )}
                    {selectedFile.sessionName && (
                      <p className="text-xs text-muted-foreground">
                        Session: {selectedFile.sessionName}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Split Points List */}
              {!isTrimmerMode && splitPoints.length > 0 && (
                <div className="mt-4">
                  <h3 className="font-medium mb-2">Split Points:</h3>
                  <div className="flex flex-wrap gap-2">
                    {splitPoints.map((point, index) => (
                      <div key={point.id} className="flex items-center gap-2 px-3 py-1 bg-primary/10 rounded-full">
                        <span className="text-sm">
                          Segment {index + 1} ({getSegmentDuration(index)})
                        </span>
                        <button onClick={() => removeSplitPoint(point.id)}>
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          </div>

          {/* Controls */}
          <div className="lg:col-span-1">
            <Card className="p-6">
              {isTrimmerMode ? (
                <>
                  <h3 className="text-lg font-semibold mb-4">Trim Settings</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <Label>Start Time (HH:MM:SS)</Label>
                      <div className="flex gap-2">
                        <Input 
                          value={formatTime(trimStart)}
                          onChange={(e) => {
                            // Parse time input
                          }}
                        />
                        <Button
                          variant="outline"
                          onClick={() => setTrimStart(currentTime)}
                        >
                          Use Current
                        </Button>
                      </div>
                    </div>
                    
                    <div>
                      <Label>End Time (HH:MM:SS)</Label>
                      <div className="flex gap-2">
                        <Input 
                          value={formatTime(trimEnd)}
                          onChange={(e) => {
                            // Parse time input
                          }}
                        />
                        <Button
                          variant="outline"
                          onClick={() => setTrimEnd(currentTime)}
                        >
                          Use Current
                        </Button>
                      </div>
                    </div>
                    
                    <div>
                      <Label>Output Name (optional)</Label>
                      <Input
                        placeholder="Trimmed video name"
                        value={trimLabel}
                        onChange={(e) => setTrimLabel(e.target.value)}
                      />
                    </div>
                    
                    <div className="text-sm text-muted-foreground text-center">
                      Current: {formatTime(currentTime)}
                    </div>
                    
                    <Button 
                      className="w-full"
                      disabled={isProcessing || trimStart >= trimEnd}
                      onClick={async () => {
                        if (!selectedFile?.s3Url) {
                          toast({
                            title: "No video selected",
                            description: "Please select a video to trim",
                            variant: "destructive"
                          });
                          return;
                        }

                        if (trimStart >= trimEnd) {
                          toast({
                            title: "Invalid trim range",
                            description: "End time must be after start time",
                            variant: "destructive"
                          });
                          return;
                        }

                        // Get or upload to S3
                        let videoKey = '';
                        let sourceBucket: string | undefined;

                        if (selectedFile.s3Key) {
                          // Already has S3 key (imported or previously uploaded)
                          videoKey = selectedFile.s3Key.replace(/^hey-joe-video-rotator\//, '');
                          sourceBucket = selectedFile.s3Bucket || (selectedFile.imported ? 'hey-joe-videos' : undefined);
                        } else if (selectedFile.localFile) {
                          // Need to upload local file first
                          toast({
                            title: "Uploading video",
                            description: `Uploading ${selectedFile.name} to process...`,
                          });

                          try {
                            const uploadResult = await uploadLocalFile(selectedFile);
                            if (!uploadResult) {
                              throw new Error('Upload failed');
                            }
                            videoKey = uploadResult.s3Key;
                            sourceBucket = uploadResult.s3Bucket;

                            // Update the file with S3 info for future operations
                            setFiles(prev => prev.map(f =>
                              f.id === selectedFile.id
                                ? { ...f, s3Key: videoKey, s3Bucket: sourceBucket }
                                : f
                            ));
                            setSelectedFile(prev => prev ? { ...prev, s3Key: videoKey, s3Bucket: sourceBucket } : prev);
                          } catch (error) {
                            toast({
                              title: "Upload failed",
                              description: error instanceof Error ? error.message : "Failed to upload video",
                              variant: "destructive"
                            });
                            return;
                          }
                        } else {
                          toast({
                            title: "Cannot process video",
                            description: "Video must be uploaded or imported from casting",
                            variant: "destructive"
                          });
                          return;
                        }

                        // Process trim operation
                        processOperation({
                          type: 'trim',
                          videoKey,
                          startTime: trimStart,
                          endTime: trimEnd,
                          label: trimLabel || `${selectedFile.name} - Trimmed`,
                          fileName: selectedFile.name,
                          sourceBucket,
                          splitPoints: [{
                            time: trimEnd,
                            label: trimLabel || 'Trimmed Video'
                          }],
                          trimStart: trimStart,
                          originalFile: selectedFile
                        });
                        
                        // Reset trim settings
                        setTrimStart(0);
                        setTrimEnd(duration);
                        setTrimLabel('');
                      }}
                    >
                      <Scissors className="w-4 h-4 mr-2" />
                      Trim Video
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <h3 className="text-lg font-semibold mb-4">Add Split Points</h3>
                  
                  <div className="space-y-4">
                    <Button
                      className="w-full"
                      onClick={addSplitAtCurrentTime}
                    >
                      <Scissors className="w-4 h-4 mr-2" />
                      Split at Current Time
                    </Button>
                    
                    <div className="text-sm text-muted-foreground text-center">
                      Current: {formatTime(currentTime)}
                    </div>
                    
                    <Button
                      className="w-full"
                      disabled={splitPoints.length === 0 || isProcessing}
                      onClick={async () => {
                        if (!selectedFile?.s3Url) {
                          toast({
                            title: "No video selected",
                            description: "Please select a video to split",
                            variant: "destructive"
                          });
                          return;
                        }

                        // Get or upload to S3
                        let videoKey = '';
                        let sourceBucket: string | undefined;

                        if (selectedFile.s3Key) {
                          // Already has S3 key (imported or previously uploaded)
                          videoKey = selectedFile.s3Key.replace(/^hey-joe-video-rotator\//, '');
                          sourceBucket = selectedFile.s3Bucket || (selectedFile.imported ? 'hey-joe-videos' : undefined);
                        } else if (selectedFile.localFile) {
                          // Need to upload local file first
                          toast({
                            title: "Uploading video",
                            description: `Uploading ${selectedFile.name} to process...`,
                          });

                          try {
                            const uploadResult = await uploadLocalFile(selectedFile);
                            if (!uploadResult) {
                              throw new Error('Upload failed');
                            }
                            videoKey = uploadResult.s3Key;
                            sourceBucket = uploadResult.s3Bucket;

                            // Update the file with S3 info for future operations
                            setFiles(prev => prev.map(f =>
                              f.id === selectedFile.id
                                ? { ...f, s3Key: videoKey, s3Bucket: sourceBucket }
                                : f
                            ));
                            setSelectedFile(prev => prev ? { ...prev, s3Key: videoKey, s3Bucket: sourceBucket } : prev);
                          } catch (error) {
                            toast({
                              title: "Upload failed",
                              description: error instanceof Error ? error.message : "Failed to upload video",
                              variant: "destructive"
                            });
                            return;
                          }
                        } else {
                          toast({
                            title: "Cannot process video",
                            description: "Video must be uploaded or imported from casting",
                            variant: "destructive"
                          });
                          return;
                        }

                        // Process split operation
                        processOperation({
                          type: 'split',
                          videoKey,
                          splitPoints: splitPoints.map((p, i) => ({
                            time: p.time,
                            label: p.label || `Segment ${i + 1}`
                          })),
                          fileName: selectedFile.name,
                          sourceBucket,
                          label: `${selectedFile.name} - Split`,
                          originalFile: selectedFile
                        });

                        setSplitPoints([]);
                      }}
                    >
                      <Scissors className="w-4 h-4 mr-2" />
                      Split Video
                    </Button>
                  </div>
                </>
              )}
              
              {/* Operation Status */}
              {(processingOperations.length > 0 || completedOperations.length > 0) && (
                <div className="mt-6 p-4 border rounded-lg bg-muted/50">
                  <h3 className="font-semibold mb-4">Operation Status</h3>
                  
                  <div className="space-y-4">
                    {processingOperations.length > 0 && (
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium">
                            Processing: {processingOperations.length} operations
                          </span>
                        </div>
                        
                        <div className="max-h-32 overflow-y-auto space-y-1">
                          {processingOperations.map((op) => (
                            <div key={op.id} className="text-xs p-2 bg-yellow-50 rounded border border-yellow-200 flex items-center">
                              <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                              {op.type} - {op.fileName}
                              {op.label && <span className="text-muted-foreground ml-1">({op.label})</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {completedOperations.length > 0 && (
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium">
                            Completed: {completedOperations.filter(r => r.status === 'completed').length} successful, 
                            {completedOperations.filter(r => r.status === 'failed').length} failed
                          </span>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={clearCompletedOperations}
                          >
                            Clear
                          </Button>
                        </div>
                        
                        <div className="max-h-32 overflow-y-auto space-y-1">
                          {completedOperations.map((result) => (
                            <div key={result.id} className="text-xs p-2 bg-background rounded border flex justify-between">
                              <span>{result.label || result.fileName || `Result`}</span>
                              <span className={`px-1 rounded ${
                                result.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {result.status}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              <Button
                variant="outline"
                className="w-full mt-4"
                onClick={clearAll}
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Clear All
              </Button>
            </Card>
          </div>
        </div>
      )}
      </main>
    </>
  );
};